import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  BiContextProvidedValue,
  BiData,
  AddWidgetBiEvent,
  MoveWidgetClickBiEvent,
} from '@wix/da-bi/pkg/events';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/ArrowUp';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/ArrowDown';
import ArrowLeftIcon from '@wix/da-ds/pkg/Icons/ArrowLeft';
import ArrowRightIcon from '@wix/da-ds/pkg/Icons/ArrowRight';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { useModuleMeta } from '../../../../hooks/useModuleMeta';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import EditConfigButton from './EditConfigButton';
import AddColumnButton from './AddColumnButton';
import RemoveButton from './RemoveButton';
import useModuleLayoutActions from '../../../../hooks/useModuleLayoutActions';

import s from './WidgetEditControls.scss';

export interface Props {
  moduleId: number;
  className?: string;
  configPopoverComponent?: React.ComponentType<any>;
  autoShowWidgetConfig?: boolean;
  confirmRemove?: (onConfirm: Function) => void;
  direction?: 'row' | 'column';
}

const WidgetEditControls: React.FC<Props> = ({
  moduleId,
  configPopoverComponent: ConfigPopoverComponent,
  autoShowWidgetConfig,
  confirmRemove,
  className,
  direction = 'column',
}) => {
  const { t } = useTranslation();
  const [isConfigPopoverOpen, setIsConfigPopoverOpen] = useState(false);
  const { moduleMeta, updateModuleMeta } = useModuleMeta(moduleId);

  useEffect(() => {
    moduleMeta?.isNewlyAdded && autoShowWidgetConfig && openConfigPopover();
  }, [moduleId, moduleMeta?.isNewlyAdded, autoShowWidgetConfig]);

  const {
    canAddModuleColumn,
    canEditModule,
    canMoveModuleUp,
    canMoveModuleDown,
    canRemoveModule,
    addModuleColumn,
    moveModuleUp,
    moveModuleDown,
    removeModule,
  } = useModuleLayoutActions(moduleId);
  if (
    !canEditModule &&
    !canMoveModuleUp &&
    !canMoveModuleDown &&
    !canAddModuleColumn &&
    !canRemoveModule
  ) {
    return null;
  }

  const openConfigPopover = () => {
    setIsConfigPopoverOpen(true);
  };

  const closeConfigPopover = () => {
    setIsConfigPopoverOpen(false);
    updateModuleMeta({ isNewlyAdded: false });
  };

  return (
    <div className={classnames(s['root'], className)}>
      {canMoveModuleUp && moveModuleUp && (
        <SimpleTooltip
          usePortal
          text={t(direction === 'column' ? 'widgets.common.moveUp' : 'widgets.common.moveLeft')}
        >
          <IconButton
            icon={direction === 'column' ? ArrowUpIcon : ArrowLeftIcon}
            variant="edit-purple"
            size="medium"
            onClick={moveModuleUp}
            className={s['button']}
            data-role="edit-control"
            biData={BiData<MoveWidgetClickBiEvent>({
              evid: 281,
              direction: direction === 'column' ? 'up' : 'left',
              widgetname: BiContextProvidedValue,
              widgetzone: BiContextProvidedValue,
            })}
            aria-label={t(
              direction === 'column' ? 'widgets.common.moveUp' : 'widgets.common.moveLeft'
            )}
          />
        </SimpleTooltip>
      )}
      {canMoveModuleDown && moveModuleDown && (
        <SimpleTooltip
          usePortal
          text={t(direction === 'column' ? 'widgets.common.moveDown' : 'widgets.common.moveRight')}
        >
          <IconButton
            icon={direction === 'column' ? ArrowDownIcon : ArrowRightIcon}
            variant="edit-purple"
            size="medium"
            onClick={moveModuleDown}
            className={s['button']}
            data-role="edit-control"
            biData={BiData<MoveWidgetClickBiEvent>({
              evid: 281,
              direction: direction === 'column' ? 'down' : 'right',
              widgetname: BiContextProvidedValue,
              widgetzone: BiContextProvidedValue,
            })}
            aria-label={t(
              direction === 'column' ? 'widgets.common.moveDown' : 'widgets.common.moveRight'
            )}
          />
        </SimpleTooltip>
      )}
      {canRemoveModule && removeModule && (
        <RemoveButton
          className={s['button']}
          moduleId={moduleId}
          confirm={confirmRemove}
          onRemove={removeModule}
        />
      )}
      {canEditModule && ConfigPopoverComponent && (
        <EditConfigButton className={s['button']} onClick={openConfigPopover} />
      )}
      {isConfigPopoverOpen && ConfigPopoverComponent && (
        <ConfigPopoverComponent moduleId={moduleId} onClose={closeConfigPopover} />
      )}
      {canAddModuleColumn && addModuleColumn && (
        <AddColumnButton
          className={s['button']}
          onClick={addModuleColumn}
          biData={BiData<AddWidgetBiEvent>({
            evid: 283,
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
        />
      )}
    </div>
  );
};

WidgetEditControls.displayName = 'WidgetEditControls';

export default WidgetEditControls;
